import * as React from 'react';
import { marketSessionPhase, type MarketSessionPhase } from 'utils/shared/time/marketSession';

/**
 * One wall clock for every session-derived control in the app.
 *
 * The status dot in the header, the live-price dot beside a number, and the auto-refresh pill all
 * answer the same question — "which US-equity session is running right now?" — and a trader reading
 * two of them at once must never see them disagree. They each used to own a `setInterval`, so at a
 * boundary like the 9:30 bell one could flip a tick ahead of another. This is the single source: one
 * interval, read by every consumer through `useSyncExternalStore`, so they all turn over together.
 *
 * It is also subscription-free — a component that needs only the session, not a price, reads the
 * clock here without opening the realtime price socket.
 */

/** 30s is finer than any boundary these controls report, and coarse enough to cost nothing. */
export const MARKET_CLOCK_INTERVAL_MS = 30_000;

/** Null until the first client subscription reads the clock, so server markup stays clock-free. */
let currentMs: number | null = null;
let intervalId: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

const tick = () => {
  currentMs = Date.now();
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  // The first subscriber seeds the clock immediately and starts the one shared interval.
  if (intervalId === null) {
    tick();
    intervalId = setInterval(tick, MARKET_CLOCK_INTERVAL_MS);
  }
  return () => {
    listeners.delete(listener);
    // Stop the clock once nothing is watching it, so it never ticks on a page that dropped it.
    if (listeners.size === 0 && intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
};

const getSnapshot = (): number | null => currentMs;
const getServerSnapshot = (): number | null => null;

/**
 * The shared wall clock in epoch ms, or `null` before it has first been read on the client.
 *
 * Resolved after mount for the hydration reason the status dot documents: three of the host pages
 * are pre-rendered, so a clock read during render would bake a build-time instant into the HTML and
 * mismatch on hydration — and a mismatch that high in the tree discards the whole SSR root.
 */
export const useMarketClockMs = (): number | null =>
  React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

/** The running US-equity session, or `null` until the shared clock has been read on the client. */
export const useMarketSessionPhase = (): MarketSessionPhase | null => {
  const nowMs = useMarketClockMs();
  return nowMs === null ? null : marketSessionPhase(nowMs);
};
